'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '@/lib/stores/theme';
import { useAuthStore } from '@/lib/stores/auth';
import { CartBadge } from './CartBadge';
import { NotificationBell } from './NotificationBell';
import { NotificationPanel } from './NotificationPanel';
import { UserDropdown } from './UserDropdown';
import { Button } from './ui/Button';
import { Sun, Moon, Monitor, ChevronDown, Search } from 'lucide-react';

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const { theme, setTheme } = useThemeStore();
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // 优先从 URL 路径读取 locale，避免 i18n 异步切换导致的语言不一致
  const urlLocale = location.pathname.split('/')[1] || 'en';
  const displayLocale = (['en', 'zh-CN'].includes(urlLocale) ? urlLocale : i18n.language) || 'en';

  // Language dropdown state
  const [localeMenuOpen, setLocaleMenuOpen] = useState(false);
  // 语言下拉同样 portal 到 body（脱离 nav.glass-navbar 的 backdrop-filter 层叠上下文），
  // 否则背景虚化够不到页面内容（与 UserDropdown 同一问题，见 backdrop-filter 坑）。
  const [localePos, setLocalePos] = useState<{ top: number; left: number } | null>(null);
  const localeTriggerRef = useRef<HTMLButtonElement>(null);
  const localeMenuRef = useRef<HTMLDivElement>(null);
  const updateLocalePos = useCallback(() => {
    if (!localeTriggerRef.current) return;
    const r = localeTriggerRef.current.getBoundingClientRect();
    setLocalePos({ top: r.bottom + 8, left: r.right - 160 }); // w-40 = 160px
  }, []);

  const toggleLocale = (newLocale: string) => {
    // 乐观更新 store user.locale，避免 LocaleSync 把刚切换的 URL 又跳回旧偏好
    const u = useAuthStore.getState().user;
    if (u) useAuthStore.getState().setUser({ ...u, locale: newLocale });
    i18n.changeLanguage(newLocale);
    const newPath = location.pathname.replace(/^\/[^\/]+/, `/${newLocale}`);
    navigate(newPath);
    setLocaleMenuOpen(false);
  };

  const toggleTheme = () => {
    const next: Record<string, 'light' | 'dark' | 'system'> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    setTheme(next[theme]);
  };

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="h-4 w-4" />;
    if (theme === 'dark') return <Moon className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  // 语言下拉：打开时接管 outside-click（同时考察触发按钮与菜单）+ 滚动/缩放重算位置。
  useEffect(() => {
    if (!localeMenuOpen) return;
    updateLocalePos();
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (localeTriggerRef.current?.contains(t)) return;
      if (localeMenuRef.current?.contains(t)) return;
      setLocaleMenuOpen(false);
    }
    function onLayout() {
      updateLocalePos();
    }
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onLayout, true);
    window.addEventListener('resize', onLayout);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onLayout, true);
      window.removeEventListener('resize', onLayout);
    };
  }, [localeMenuOpen, updateLocalePos]);

  return (
    <nav className="sticky top-0 z-50 glass-navbar">
      <div className="mx-auto max-w-7xl px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to={`/${displayLocale}`} className="flex items-center gap-2.5">
          <img src="/images/brand/logo-nav.png" alt="ClawRent" className="h-9 w-auto" />
          <span className="text-xl font-bold">
            Claw<span className="text-brand-500">Rent</span>
          </span>
        </Link>

        {/* Actions (right-aligned: nav links + tools + auth) */}
        <div className="flex items-center gap-2">
          {/* Nav Links — right aligned */}
          <div className="hidden md:flex items-center gap-4 mr-2">
            <Link to={`/${displayLocale}/marketplace`} className="text-sm hover:text-brand-500 transition-colors">
              {t('nav.marketplace')}
            </Link>
            <Link to={`/${displayLocale}/docs`} className="text-sm hover:text-brand-500 transition-colors">
              {t('nav.docs')}
            </Link>
          </div>

          <div className="h-5 w-px bg-border hidden md:block" />

          {/* Command palette trigger */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('open-command-palette'))}
            aria-label={t('nav.openCommandPalette')}
            title={t('nav.openCommandPalette')}
            className="hidden md:flex items-center gap-2 text-sm h-9 px-3 rounded-lg border border-border hover:border-brand-500 transition-colors text-muted-foreground"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="text-xs">{t('nav.search')}</span>
            <kbd className="text-[10px] px-1 py-0.5 rounded bg-surface-2 border border-border">⌘K</kbd>
          </button>

          {/* Language Switcher (Dropdown) */}
          <div className="relative">
            <button
              ref={localeTriggerRef}
              onClick={() => setLocaleMenuOpen(!localeMenuOpen)}
              className="text-sm h-9 px-3 rounded-lg border border-border hover:border-brand-500 transition-colors flex items-center gap-1.5"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              {displayLocale === 'en' ? 'English' : '简体中文'}
              <ChevronDown className={`h-3 w-3 opacity-60 transition-transform ${localeMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu — portal 到 body，脱离 nav 的 backdrop-filter 层叠上下文 */}
            {localeMenuOpen &&
              localePos &&
              createPortal(
                <div
                  ref={localeMenuRef}
                  style={{ position: 'fixed', top: localePos.top, left: localePos.left, width: 160 }}
                  className="glass-popover rounded-lg py-1 z-50"
                >
                  <button
                    onClick={() => toggleLocale('en')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-brand-500/10 transition-colors flex items-center justify-between ${displayLocale === 'en' ? 'text-brand-500 font-medium' : ''}`}
                  >
                    English
                    {displayLocale === 'en' && <span className="text-brand-500">✓</span>}
                  </button>
                  <button
                    onClick={() => toggleLocale('zh-CN')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-brand-500/10 transition-colors flex items-center justify-between ${displayLocale === 'zh-CN' ? 'text-brand-500 font-medium' : ''}`}
                  >
                    简体中文
                    {displayLocale === 'zh-CN' && <span className="text-brand-500">✓</span>}
                  </button>
                </div>,
                document.body,
              )}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="h-9 w-9 rounded-lg border border-border hover:border-brand-500 transition-colors flex items-center justify-center"
          >
            {getThemeIcon()}
          </button>

          {isLoading ? (
            <div className="flex items-center gap-3">
              <div className="w-20 h-4 bg-surface-2 rounded animate-pulse" />
              <div className="w-16 h-8 bg-surface-2 rounded-lg animate-pulse" />
            </div>
          ) : user ? (
            <>
              {/* Dashboard Link */}
              <Link
                to={`/${displayLocale}/dashboard`}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden md:block"
              >
                {t('nav.dashboard')}
              </Link>

              {/* Cart Badge (icon with count bubble) */}
              <CartBadge />

              {/* Notification Bell + Panel */}
              <div className="relative">
                <NotificationBell />
                <NotificationPanel />
              </div>

              {/* User Dropdown (avatar + name) */}
              <UserDropdown />
            </>
          ) : (
            <Button
              variant="default"
              size="sm"
              className="bg-brand-600 hover:bg-brand-700 text-white font-medium"
              onClick={() => navigate(`/${displayLocale}/login?redirect=${encodeURIComponent(location.pathname)}`)}
            >
              {t('nav.signIn')}
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
}
